import { FC, useState } from 'react';
import { useContextSelector as useCS } from 'use-context-selector';
import { Modal } from '@mui/material';
import { CloseIcon } from 'assets';
import { TUpdate } from 'domain/types';
import { Button, Input } from 'view/components';
import * as CS from 'view/modals/styled';
import {
  GlobalConfigContext,
  TGlobalConfigContextState,
} from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { JobPostsDropdownAutocomplete } from 'view/pages/candidates/components';
import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { useApplyForJobPost } from 'view/pages/createJobPost/hooks/useApplyForJobPost';
import { useNavigate } from 'react-router';

const FormFields: FC<{
  isLoading: boolean;
  jobPostsValue: any;
  setJobPostsValue: any;
  onClose: any;
  jobSeekerId: any;
}> = ({ isLoading, jobPostsValue, setJobPostsValue, onClose, jobSeekerId }) => {
  const navigate = useNavigate();
  const { watch } = useFormContext<any>();

  const isDisabled = !jobPostsValue || !watch('initialMessage') || isLoading;

  return (
    <>
      <CS.ModalMainBox>
        <CS.ModalMainHeader style={{ marginBottom: '32px' }}>
          <CS.ModalMainHeaderTitle>Start the Conversation</CS.ModalMainHeaderTitle>
          <CS.ModalMainHeaderSubTitle>
            {
              'Send a message to express your interest and invite them to connect. \n (They’ll be notified when you reach out.)'
            }
          </CS.ModalMainHeaderSubTitle>
        </CS.ModalMainHeader>

        <JobPostsDropdownAutocomplete
          label="Job Posts *"
          setValue={setJobPostsValue}
          jobPostsValue={jobPostsValue}
          getJobPostInfoById={() => {}}
          marginbottom={16}
          jobSeekerId={jobSeekerId}
        />
        {jobPostsValue && !jobPostsValue?.applicationStatus && (
          <Input
            name="initialMessage"
            label="Comment *"
            placeholder="e.g., “Hi, your experience really stood out to us. Would you be open to a quick conversation about this opportunity?”"
            multiline
            rows={6}
            marginbottom={32}
          />
        )}
      </CS.ModalMainBox>
      <CS.ModalMainFooter>
        <Button
          variant="outlined"
          text="Cancel"
          style={{ width: '100%', minHeight: '50px' }}
          onClick={onClose}
        />
        {jobPostsValue && !jobPostsValue?.applicationStatus ? (
          <Button variant="contained" text="Send Message" type="submit" disabled={isDisabled} />
        ) : (
          <Button
            variant="contained"
            text="Go to chat"
            onClick={() => {
              navigate(
                jobPostsValue?.applicationStatus?.chatId
                  ? `/messenger/chats?conversationId=${jobPostsValue?.applicationStatus?.chatId}`
                  : '/messenger/chats'
              );
              onClose();
            }}
            disabled={!jobPostsValue || !jobPostsValue?.applicationStatus?.chatId}
          />
        )}
      </CS.ModalMainFooter>
    </>
  );
};

const RecruiterStartConversationModal: FC<{ hideCloseButton?: boolean; jobSeekerId: any }> = ({
  hideCloseButton = false,
  jobSeekerId,
}) => {
  const navigate = useNavigate();
  const [jobPostsValue, setJobPostsValue] = useState<any>(null);
  const isOpenRecruiterStartConversationModal: boolean = useCS(
    GlobalConfigContext,
    globalConfigSelectors.isOpenRecruiterStartConversationModal
  );

  const update: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );

  const { isLoading, applyForJobPost } = useApplyForJobPost();

  const onClose = () => {
    setJobPostsValue(null);
    if (update) update({ isOpenRecruiterStartConversationModal: false });
  };

  const onSubmit = (formData: any) => {
    const dataToSend = {
      jobSeekerId,
      jobPostId: jobPostsValue?.id,
      applicationType: 'INVITE',
      initialMessage: formData.initialMessage,
    };

    applyForJobPost(dataToSend, (chatId: any) => {
      onClose();
      navigate(chatId ? `/messenger/chats?conversationId=${chatId}` : '/messenger/chats');
    });
  };

  return (
    <Modal
      open={isOpenRecruiterStartConversationModal}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <CS.ModalMainWrapper>
        {!hideCloseButton ? (
          <CS.CloseIconBox onClick={onClose}>
            <CloseIcon />
          </CS.CloseIconBox>
        ) : null}
        <FormContainer defaultValues={{ initialMessage: '' }} onSuccess={onSubmit}>
          <FormFields
            isLoading={isLoading}
            jobPostsValue={jobPostsValue}
            setJobPostsValue={setJobPostsValue}
            onClose={onClose}
            jobSeekerId={jobSeekerId}
          />
        </FormContainer>
      </CS.ModalMainWrapper>
    </Modal>
  );
};

export default RecruiterStartConversationModal;
